import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { baseURL } from "services/API";

const REASON_LABELS = {
  LEGACY_INITIAL: "Initial ownership",
  PROPERTY_SOLD: "Property sold",
  TRANSFER: "Transfer",
  REASSIGN: "Reassigned",
};

const formatReason = (reason) => REASON_LABELS[reason] || reason?.replace(/_/g, " ") || "—";

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "Present");

const PropertyOwnershipHistory = ({ rentalId }) => {
  const token = useSelector((state) => state.token);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rentalId) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      try {
        const response = await axios.get(
          `${baseURL}/landlords/rentals/ownership-history?rental_id=${rentalId}`
        );
        if (!cancelled) {
          setData(response.data?.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.error || e?.response?.data?.message || "Failed to load ownership history."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [rentalId, token]);

  if (!rentalId) {
    return <Alert severity="info">No property selected.</Alert>;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const periods = Array.isArray(data?.periods) ? data.periods : [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Ownership history
        {data?.property_name ? ` — ${data.property_name}` : ""}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Timeline of landlords who have owned this property. Loaded on demand when you open this tab.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Landlord</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No ownership periods recorded.</TableCell>
              </TableRow>
            ) : (
              periods.map((period) => (
                <TableRow key={period.id}>
                  <TableCell>{period.landlord_name || `Landlord #${period.landlord_id}`}</TableCell>
                  <TableCell>{formatDate(period.effective_from)}</TableCell>
                  <TableCell>{period.is_current ? "Present" : formatDate(period.effective_to)}</TableCell>
                  <TableCell>{formatReason(period.reason)}</TableCell>
                  <TableCell>{period.notes || "—"}</TableCell>
                  <TableCell>
                    {period.is_current ? (
                      <Chip size="small" label="Current" color="primary" variant="outlined" />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PropertyOwnershipHistory;
